import { CartAddEvent } from '@theme/events';

class AddToCartWrapper extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.variantInput = this.querySelector('[data-selected-variant]');
    this.submitButton = this.querySelector('[data-add-to-cart-submit]');
    this.submitLabel = this.querySelector('[data-add-to-cart-label]');
    this.errorMessage = this.querySelector('[data-add-to-cart-error]');
    this.defaultButtonLabel = this.submitLabel?.textContent || 'Add to cart';
    this.quantityLimitMessage = this.dataset.quantityLimitMessage || 'Only one of this product can be purchased per order.';

    const directGroups = Array.from(this.form?.children || []).filter((element) =>
      element.classList.contains('group-block')
    );
    const pricingGroup = directGroups[0];
    directGroups.slice(2).forEach((group) => {
      group.hidden = true;
      group.setAttribute('aria-hidden', 'true');
    });

    const pricingContent = pricingGroup?.querySelector(':scope > .group-block-content');
    const cards = Array.from(pricingContent?.children || []).filter((element) =>
      element.classList.contains('group-block')
    );

    this.options = [
      this.prepareOption(cards[0], 'monthly'),
      this.prepareOption(cards[1], 'prepaid'),
    ].filter(Boolean);

    this.options.forEach((option) => {
      option.addEventListener('click', this.onOptionClick);
      option.addEventListener('keydown', this.onOptionKeydown);
    });
    this.form?.addEventListener('submit', this.onSubmit);

    const preferredOption = this.options.find(
      (option) =>
        option.dataset.optionType === this.dataset.defaultOption &&
        option.dataset.available === 'true'
    );
    const initial = preferredOption || this.options.find((option) => option.dataset.available === 'true');
    if (initial) this.selectOption(initial);
    else this.setError('Select the Monthly and Prepaid products in the block settings.');
  }

  disconnectedCallback() {
    this.options?.forEach((option) => {
      option.removeEventListener('click', this.onOptionClick);
      option.removeEventListener('keydown', this.onOptionKeydown);
    });
    this.form?.removeEventListener('submit', this.onSubmit);
  }

  prepareOption(element, type) {
    if (!(element instanceof HTMLElement)) return null;
    const prefix = type === 'monthly' ? 'monthly' : 'prepaid';
    element.classList.add('add-to-cart-wrapper-block__option');
    element.dataset.optionType = type;
    element.dataset.variantId = this.dataset[`${prefix}VariantId`] || '';
    element.dataset.productId = this.dataset[`${prefix}ProductId`] || '';
    element.dataset.sellingPlanId = this.dataset[`${prefix}SellingPlanId`] || '';
    element.dataset.available = element.dataset.variantId ? 'true' : 'false';
    element.setAttribute('role', 'radio');
    element.setAttribute('aria-checked', 'false');
    element.setAttribute('aria-disabled', String(element.dataset.available !== 'true'));
    element.tabIndex = element.dataset.available === 'true' ? 0 : -1;
    return element;
  }

  onOptionClick = (event) => {
    const option = event.currentTarget;
    if (option.dataset.available === 'true') this.selectOption(option);
  };

  onOptionKeydown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    const option = event.currentTarget;
    if (option.dataset.available === 'true') this.selectOption(option);
  };

  selectOption(option) {
    this.options.forEach((item) => item.setAttribute('aria-checked', String(item === option)));
    this.selectedOption = option;
    if (this.variantInput) this.variantInput.value = option.dataset.variantId;
    if (this.submitButton) this.submitButton.disabled = false;
    this.setError('');
  }

  onSubmit = async (event) => {
    event.preventDefault();
    if (!this.selectedOption || !this.variantInput?.value) {
      this.setError('Please select a purchase option.');
      return;
    }

    const mailingDate = this.form.querySelector('[name="properties[First mailing]"]')?.value;
    if (!mailingDate) {
      this.setError('Please select an available mailing date.');
      return;
    }

    if (this.selectedOption.dataset.optionType === 'monthly' && !this.selectedOption.dataset.sellingPlanId) {
      this.setError('The Monthly subscription plan is currently unavailable.');
      return;
    }

    this.setLoading(true);
    this.setError('');

    try {
      const existingCartResponse = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
        headers: { Accept: 'application/json' },
      });
      const existingCart = await existingCartResponse.json();
      const selectedVariantId = Number(this.variantInput.value);

      if (existingCart.items?.some((item) => Number(item.variant_id) === selectedVariantId)) {
        this.setError(this.quantityLimitMessage);
        return;
      }

      const sectionIds = Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
        .map((element) => element.dataset.sectionId)
        .filter(Boolean);
      const item = {
        id: Number(this.variantInput.value),
        quantity: 1,
        properties: { 'First mailing': mailingDate },
      };

      if (this.selectedOption.dataset.sellingPlanId) {
        item.selling_plan = Number(this.selectedOption.dataset.sellingPlanId);
      }

      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [item],
          sections: sectionIds,
          sections_url: window.location.pathname,
        }),
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || 'Unable to add this product.');

      const cartResponse = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
        headers: { Accept: 'application/json' },
      });
      const cart = await cartResponse.json();
      this.dispatchEvent(new CartAddEvent(cart, this.variantInput.value, {
        source: 'add-to-cart-wrapper',
        itemCount: 1,
        productId: this.selectedOption.dataset.productId,
        variantId: this.variantInput.value,
        sections: result.sections,
      }));

      if (this.submitLabel) this.submitLabel.textContent = 'Added';
      window.setTimeout(() => {
        if (this.submitLabel) this.submitLabel.textContent = this.defaultButtonLabel;
      }, 1500);
    } catch (error) {
      this.setError(error instanceof Error ? error.message : 'Unable to add this product.');
    } finally {
      this.setLoading(false);
    }
  };

  setLoading(loading) {
    if (this.submitButton) this.submitButton.disabled = loading;
    this.toggleAttribute('aria-busy', loading);
    if (loading && this.submitLabel) this.submitLabel.textContent = 'Adding…';
    if (!loading && this.submitLabel?.textContent === 'Adding…') this.submitLabel.textContent = this.defaultButtonLabel;
  }

  setError(message) {
    if (!this.errorMessage) return;
    this.errorMessage.textContent = message;
    this.errorMessage.hidden = !message;
  }
}

if (!customElements.get('add-to-cart-wrapper')) {
  customElements.define('add-to-cart-wrapper', AddToCartWrapper);
}
